import { useState } from 'react';
import { Share2, Link2, Check, X, Smartphone, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const APP_URL = 'https://growdaily.fun';

const PLATFORMS = [
    { id: 'x', label: 'X', icon: 'fa-brands fa-x-twitter', color: '#000000',
      build: (u, t) => `https://twitter.com/intent/tweet?text=${t}&url=${u}` },
    { id: 'facebook', label: 'Facebook', icon: 'fa-brands fa-facebook-f', color: '#1877F2',
      build: (u) => `https://www.facebook.com/sharer/sharer.php?u=${u}` },
    { id: 'whatsapp', label: 'WhatsApp', icon: 'fa-brands fa-whatsapp', color: '#25D366',
      build: (u, t) => `https://wa.me/?text=${t}%20${u}` },
    { id: 'messenger', label: 'Messenger', icon: 'fa-brands fa-facebook-messenger', color: '#0084FF',
      build: (u) => `fb-messenger://share/?link=${u}` },
    { id: 'telegram', label: 'Telegram', icon: 'fa-brands fa-telegram', color: '#26A5E4',
      build: (u, t) => `https://t.me/share/url?url=${u}&text=${t}` },
    { id: 'reddit', label: 'Reddit', icon: 'fa-brands fa-reddit-alien', color: '#FF4500',
      build: (u, t) => `https://www.reddit.com/submit?url=${u}&title=${t}` },
    { id: 'pinterest', label: 'Pinterest', icon: 'fa-brands fa-pinterest-p', color: '#BD081C',
      build: (u, t) => `https://www.pinterest.com/pin/create/button/?url=${u}&description=${t}` },
    { id: 'sms', label: 'SMS', icon: 'fa-solid fa-comment-sms', color: '#34B7F1',
      build: (u, t) => `sms:?&body=${t}%20${u}` },
    { id: 'email', label: 'Email', icon: 'fa-solid fa-envelope', color: '#6E7A6E',
      build: (u, t) => `mailto:?subject=${t}&body=${t}%20${u}` },
];

async function copyLink(payload) {
    try {
        await navigator.clipboard.writeText(payload);
        return true;
    } catch {
        try {
            const ta = document.createElement('textarea');
            ta.value = payload;
            ta.style.position = 'fixed';
            ta.style.opacity = '0';
            document.body.appendChild(ta);
            ta.focus();
            ta.select();
            const ok = document.execCommand('copy');
            document.body.removeChild(ta);
            return ok;
        } catch {
            return false;
        }
    }
}

export default function ShareButton({ title, text, url = APP_URL, className = '', label = 'Share', resolveUrl = null }) {
    const [open, setOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const [loading, setLoading] = useState(false);
    const [override, setOverride] = useState(null);

    const eff = {
        title: override?.title || title,
        text: override?.text || text || title,
        url: override?.url || url,
    };
    const shareText = eff.text;
    const payload = `${shareText}\n${eff.url}`;
    const eu = encodeURIComponent(eff.url);
    const et = encodeURIComponent(shareText);

    const trigger = async (e) => {
        e.stopPropagation();
        if (resolveUrl) {
            setLoading(true);
            try {
                const res = await resolveUrl();
                if (res) setOverride(typeof res === 'string' ? { url: res } : res);
            } catch (err) {
                setLoading(false);
                if (err?.message !== 'abort') toast.error('Could not prepare share link');
                return;
            }
            setLoading(false);
        }
        setOpen(true);
    };

    const nativeShare = async () => {
        try {
            await navigator.share({ title: eff.title, text: shareText, url: eff.url });
            setOpen(false);
        } catch (err) {
            if (err?.name !== 'AbortError') toast.error('Sharing was blocked — try a platform below');
        }
    };

    const openPlatform = (p) => {
        const href = p.build(eu, et);
        try {
            const w = window.open(href, '_blank', 'noopener,noreferrer');
            if (!w) window.location.href = href; // fallback for app-scheme links (sms/messenger)
        } catch {
            window.location.href = href;
        }
        setOpen(false);
    };

    const doCopy = async () => {
        const ok = await copyLink(payload);
        if (ok) {
            setCopied(true);
            toast.success('Link copied — paste it in Instagram, TikTok, Snapchat or anywhere');
            setTimeout(() => setCopied(false), 2000);
        } else {
            toast.error('Could not copy automatically — long-press the link to copy');
        }
    };

    return (
        <>
            <button
                type="button"
                onClick={trigger}
                disabled={loading}
                data-testid="share-button"
                className={`inline-flex items-center gap-1.5 text-sm font-medium transition-colors disabled:opacity-50 ${className}`}
            >
                {loading ? <Loader2 size={15} className="animate-spin" /> : <Share2 size={15} strokeWidth={1.8} />} {label}
            </button>

            {open && (
                <div
                    className="fixed inset-0 z-[80] bg-brand-ink/45 backdrop-blur-sm flex items-end sm:items-center justify-center"
                    onClick={() => setOpen(false)}
                    data-testid="share-sheet-overlay"
                >
                    <div
                        className="bg-brand-bg w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl border border-brand-border p-6 animate-fade-up"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="share-sheet"
                    >
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <h3 className="font-serif text-xl text-brand-ink">Share</h3>
                                <p className="text-xs text-brand-ink3 mt-0.5 line-clamp-2">{shareText}</p>
                            </div>
                            <button data-testid="share-sheet-close" onClick={() => setOpen(false)} className="p-2 hover:bg-brand-bg2 rounded-full -mr-2 -mt-1"><X size={18} /></button>
                        </div>

                        {typeof navigator !== 'undefined' && navigator.share && (
                            <button
                                onClick={nativeShare}
                                data-testid="share-native"
                                className="w-full mb-4 inline-flex items-center justify-center gap-2 bg-brand-green text-white py-3 rounded-full text-sm font-medium hover:bg-brand-greenDark transition-colors"
                            >
                                <Smartphone size={16} /> Share via apps…
                            </button>
                        )}

                        <div className="grid grid-cols-4 gap-3">
                            {PLATFORMS.map((p) => (
                                <button
                                    key={p.id}
                                    data-testid={`share-${p.id}`}
                                    onClick={() => openPlatform(p)}
                                    className="flex flex-col items-center gap-1.5 group"
                                >
                                    <span
                                        className="w-12 h-12 rounded-2xl flex items-center justify-center text-white text-lg group-hover:-translate-y-0.5 transition-transform"
                                        style={{ backgroundColor: p.color }}
                                    >
                                        <i className={p.icon} aria-hidden="true" />
                                    </span>
                                    <span className="text-[11px] text-brand-ink2">{p.label}</span>
                                </button>
                            ))}
                            <button
                                data-testid="share-copy"
                                onClick={doCopy}
                                className="flex flex-col items-center gap-1.5 group"
                            >
                                <span className="w-12 h-12 rounded-2xl flex items-center justify-center bg-brand-ink2 text-white group-hover:-translate-y-0.5 transition-transform">
                                    {copied ? <Check size={18} /> : <Link2 size={18} />}
                                </span>
                                <span className="text-[11px] text-brand-ink2">Copy link</span>
                            </button>
                        </div>

                        <p className="text-[11px] text-brand-ink3 mt-4 leading-relaxed text-center">
                            For Instagram, TikTok, Snapchat or Patreon, tap <strong>Copy link</strong> and paste it there — or use <strong>Share via apps…</strong> on your phone.
                        </p>
                    </div>
                </div>
            )}
        </>
    );
}
